import React from 'react';
import { PlayCircle, ClipboardList, Rocket } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSiteContent } from '../../context/SiteContentContext';

const iconMap = [PlayCircle, ClipboardList, Rocket];

const HowItWorks = () => {
  const { siteContent } = useSiteContent();
  const howItWorks = siteContent?.howItWorks || {};

  if (howItWorks.visible === false) return null;

  const steps = howItWorks.steps || [];

  return (
    <section className="py-14 sm:py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10 sm:mb-14"
        >
          <span className="text-xs sm:text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 sm:mb-4 block">
            {howItWorks.label || 'Getting Started'}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
            {howItWorks.title || 'How It Works'}
          </h2>
        </motion.div>

        {/* Desktop */}
        <div className="hidden sm:grid sm:grid-cols-3 gap-6 md:gap-8 relative">
          <div className="absolute top-9 md:top-10 left-[20%] right-[20%] h-[2px] bg-slate-100" />
          
          {steps.map((step, index) => {
            const Icon = iconMap[index % 3];
            const stepKey = step.title?.en || step.title || `step-${index}`;
            return (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="relative text-center"
              >
                <div className="relative inline-flex items-center justify-center mb-4 sm:mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-50 rounded-xl md:rounded-2xl flex items-center justify-center border border-slate-100">
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-slate-700" strokeWidth={1.5} />
                  </div>
                  <span className="absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2 w-6 h-6 md:w-8 md:h-8 bg-slate-900 text-white text-xs md:text-sm font-semibold rounded-full flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-500">
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile */}
        <div className="sm:hidden grid grid-cols-3 gap-2">
          {steps.map((step, index) => {
            const Icon = iconMap[index % 3];
            const stepKey = step.title?.en || step.title || `step-mobile-${index}`;
            return (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100"
              >
                <div className="relative inline-flex items-center justify-center mb-2">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-slate-700" strokeWidth={1.5} />
                  </div>
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-slate-900 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-xs font-semibold text-slate-900 mb-0.5">
                  {step.title}
                </h3>
                <p className="text-[10px] text-slate-500 leading-tight">
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
